import i18n from 'i18n-js';
import React from 'react';
import ProductivityTimeline from '@components/common/ProductivityTimeline';
import { RenderTimeParams, GetProductiveTimeBlockParams, ProductiveTimeBlock } from '@components/machines/components/detailsComponents/activeMachine/interfaces';
import { TimeCategory } from '@interfaces/base';
import { ncFileStatuses } from '@helpers';

export const renderTime = ({ h, m, s }: RenderTimeParams) => {
  const hN = Number(h);
  const mN = Number(m);
  const sN = Number(s);

  const seconds = (() => (sN > 0 ? ` ${sN}${i18n.t('s')}` : ''))();

  if (hN > 0) {
    return `${hN}${i18n.t('h')} ${mN}${i18n.t('m')}${seconds}`;
  }
  if (mN > 0) {
    return `${mN}${i18n.t('m')}${seconds}`;
  }

  return `${sN}${i18n.t('s')}`;
};

export const getProductiveTimeBlock = ({
  C,
  batch,
  timeDifferenceConfigs,
  ncFileStatus: ncFileStatusFromProps,
  styles,
}: GetProductiveTimeBlockParams): ProductiveTimeBlock[] => {
  const {
    actualTotalTimeTotalInSeconds: actualTotal = 0,
    actualActiveTimeTotalInSeconds = 0,
    actualChangeTimeTotalInSeconds = 0,
    actualSetupTimeInSeconds = 0,
    scheduledTotalTimeTotalInSeconds: scheduledTotal = 0,
    scheduledActiveTimeTotalInSeconds = 0,
    scheduledChangeTimeTotalInSeconds = 0,
    scheduledSetupTimeInSeconds = 0,
    idealTotalTimeTotalInSeconds: idealTotal = 0,
    idealActiveTimeTotalInSeconds = 0,
    idealChangeTimeTotalInSeconds = 0,
    idealSetupTimeInSeconds = 0,
    paidTotalTimeTotalInSeconds: paidTotal = 0,
    paidActiveTimeTotalInSeconds = 0,
    paidChangeTimeTotalInSeconds = 0,
    paidSetupTimeInSeconds = 0,
  } = batch || {};

  const maxValue = Math.max(actualTotal, paidTotal, idealTotal, scheduledTotal);

  const ncFileStatus = ncFileStatusFromProps ?? batch?.ncFileStatus;
  const isIgnoredNCFile = ncFileStatus === ncFileStatuses.Ignored;

  return [
    {
      Component: () => (
        <ProductivityTimeline
          {...{
            C,
            icon: 'actual',
            text: 'Actual',
            totalTime: actualTotal,
            maxValue,
            color: 'Status/Light/Productive',
            active: actualActiveTimeTotalInSeconds,
            change: actualChangeTimeTotalInSeconds,
            setup: actualSetupTimeInSeconds,
            borderRadiusTop: true,
            width: 15,
            height: 15,
            styles,
            shouldHideTimeline: isIgnoredNCFile,
          }}
        />
      ),
      name: TimeCategory.ACTUAL,
      key: 'batchActual',
    },
    {
      Component: () => (
        <ProductivityTimeline
          {...{
            C,
            icon: 'runningBunny',
            text: 'Ideal',
            totalTime: idealTotal,
            maxValue,
            color: 'Label/Light/Tertiary',
            active: idealActiveTimeTotalInSeconds,
            change: idealChangeTimeTotalInSeconds,
            setup: idealSetupTimeInSeconds,
            width: 16,
            height: 20,
            timeDifferenceConfig: timeDifferenceConfigs?.ideal,
            styles,
          }}
        />
      ),
      name: TimeCategory.IDEAL,
      key: 'batchIdeal',
    },
    {
      Component: () => (
        <ProductivityTimeline
          {...{
            C,
            icon: 'scheduledTime',
            text: 'Scheduled',
            totalTime: scheduledTotal,
            maxValue,
            color: 'Selected/Light/Primary',
            active: scheduledActiveTimeTotalInSeconds,
            change: scheduledChangeTimeTotalInSeconds,
            setup: scheduledSetupTimeInSeconds,
            borderRadiusBottom: true,
            width: 16,
            height: 16,
            timeDifferenceConfig: timeDifferenceConfigs?.scheduled,
            styles,
          }}
        />
      ),
      name: TimeCategory.SCHEDULED,
      key: 'batchScheduled',
    },
    {
      Component: () => (
        <ProductivityTimeline
          {...{
            C,
            icon: 'euro',
            text: 'Paid',
            totalTime: paidTotal,
            maxValue,
            color: 'Status/Light/Paid',
            active: paidActiveTimeTotalInSeconds,
            change: paidChangeTimeTotalInSeconds,
            setup: paidSetupTimeInSeconds,
            width: 11,
            height: 20,
            timeDifferenceConfig: timeDifferenceConfigs?.paid,
            styles,
          }}
        />
      ),
      name: TimeCategory.PAID,
      key: 'batchPaid',
    },
  ];
};
